import { FileSizeUnits } from '../../util/util';
import { FormDetails } from '../../empracoTypes';
import { formTexts } from './FormSelectorFns';

function ClearFiles(element: HTMLDivElement, input: HTMLInputElement, text: Element) {
  element.innerHTML = '<i class="fa-regular fa-xmark"></i>';
  element.title = formTexts.clearTitle;
  element.onclick = () => {
    input.value = '';
    element.innerHTML = '';
    text.innerHTML = formTexts.noFiles;
  };
}

function showFileErrorMessage(showError: boolean, target: HTMLInputElement, message: string | null) {
  const inputDesc = target.closest('div.node-file')?.querySelector<HTMLInputElement>('p.input-desc');
  if (inputDesc != null) {
    inputDesc.hidden = showError;
  }

  const errorDesc = target.closest('div.node-file')?.querySelector<HTMLElement>('p.error-desc');
  if (errorDesc != null) {
    if (message != null) {
      errorDesc.innerHTML = message;
    }
    errorDesc.hidden = !showError;
  }

  if (showError) {
    target.closest('div.file-input-container')?.classList.add('error-file');
  } else {
    target.closest('div.file-input-container')?.classList.remove('error-file');
  }
};

export function handleFileChanges(formDetails: FormDetails, formEl: HTMLFormElement) {
  const fileInputs = formEl.querySelectorAll('input[type=file]');
  const formLimit = formDetails.form.settings.maxUploadFileSize;
  const globalLimit = formDetails.globalMaxUploadFileSize;

  // Calculates in megabytes
  const getFileSizeLimit = () => {

    if (formLimit && formLimit > 0) {
      return formLimit * FileSizeUnits.MiB;
    }

    if (globalLimit && globalLimit > 0) {
      return globalLimit * FileSizeUnits.MiB;
    }

    return 0;
  };

  const fileSizeLimit = getFileSizeLimit();


  const addLoadingSpinner = (elm?: Element | null) => {

    if (!elm) {
      return () => {
        // DO NOTHING
      };
    }
    setTimeout(() => {
      elm.innerHTML = '<i class="fa-regular fa-spinner-third fa-spin"></i>';
    });
    return () => {
      elm.innerHTML = '<i class="fa-regular fa-xmark"></i>';
    };
  };

  if (fileInputs && fileInputs.length > 0) {
    fileInputs.forEach(input => {
      input.addEventListener('change', event => {

        const target = event.currentTarget as HTMLInputElement;
        if (target == null || target.value == null || target.value === '') {
          return;
        }

        const files = target.files;
        if (!files) {
          return;
        }
        const clearEl = input.parentElement?.nextElementSibling as HTMLDivElement;
        const removeLoadingSpinner = addLoadingSpinner(clearEl);
        const textEl = target.nextElementSibling?.querySelector('.upload-status');
        const fileExtensionArr = files![0].name.split('.');
        const fileExtension = '.' + fileExtensionArr[fileExtensionArr.length - 1];
        const fileSize = files
          ? Array.from(files).reduce((prev, num) => { return prev + num.size; }, 0)
          : 0;

        const getFileTypes = () => {
          return formDetails.form.inputs.find(inp => inp.uniqueName === input.id)?.allowedFiles ?? [];
        };
        const fileTypes = getFileTypes();

        const verifyFileType = () => {
          if (fileTypes && !fileTypes.length) {
            return true; // Allow all types
          }

          const type = files[0].type;
          return fileTypes.filter(s => s && s.type === type || s.type === fileExtension).length > 0;
        };

        const reset = () => {
          target.files = null;
          target.value = '';
          setTimeout(() => { clearEl.innerHTML = ''; });
          if (textEl) {
            textEl.innerHTML = formTexts.noFiles;
          }
        };

        const fileSizeAllowed = fileSizeLimit === 0 || fileSize <= fileSizeLimit;
        const fileTypesAllowed = verifyFileType();

        // File size validation error.
        if (textEl && files && !fileSizeAllowed) {
          reset();

          const mbValue = formLimit && formLimit > 0
            ? formLimit
            : globalLimit && globalLimit > 0
              ? globalLimit : 0;

          // eslint-disable-next-line max-len
          const errorMessage = `File size exceeded maximum allowed upload file size of ${mbValue} MB. Please upload less or smaller files.`;
          showFileErrorMessage(true, target, errorMessage);
          return;
        }

        // File type validation error.
        if (textEl && files && !fileTypesAllowed) {
          reset();
          const allowedTypes = fileTypes.map(m => m.type).join(', ');
          const errorMessage = `Only supports ${allowedTypes} file ${fileTypes.length > 1 ? 'formats' : 'format'}.`;
          showFileErrorMessage(true, target, errorMessage);
        }

        // File upload succeeded.
        if (textEl && clearEl && fileTypesAllowed && fileSizeAllowed) {
          setTimeout(() => {
            showFileErrorMessage(false, target, null);
            let filesText = files.length > 1 ? `${files.length} files` : files[0].name;
            textEl.innerHTML = filesText;
            ClearFiles(clearEl, target, textEl);
            removeLoadingSpinner();
            return;
          }, 400);
        }
      });
    });
  };
}